import { convertTimePointToMomentDate, convertTimePointToMomentDateWithTime, convertIndexedTimePointToDateWithoutYear, isAfterDateOperation, isAfterDateOperationForRelativeDates, isAfterDateWithTimeOperation, isAfterPropertyOperation, isAnniversaryBetweenDatesOperation, isAnniversaryNotBetweenDatesOperation, isBeforeDateOperation, isBeforeDateOperationForRelativeDates, isBeforeDateWithTimeOperation, isBeforePropertyOperation, isBetweenDatesOperation, isBetweenDatesWithTimeOperation, isEqualToDateOperation, isEqualToRelativeDateOperation, isNotEqualToRelativeDateOperation, isLessThanXDaysAgoOperation, isLessThanXDaysFromNowOperation, isMoreThanXDaysAgoOperation, isMoreThanXDaysFromNowOperation, isNotBetweenDatesOperation, isNotBetweenDatesWithTimeOperation, isNotUpdatedInLastXDaysOperation, isUpdatedAfterPropertyOperation, isUpdatedBeforePropertyOperation, isUpdatedInLastXDaysOperation } from '../timeFilters/timeFiltersUtils';
import 'I18n/init/data/allTimezones';
import { hasDateTimeHoursFiltering, hasILSPresetDynamicDateFilters, hasNewIlsTimeFiltersEnabled } from 'customer-data-filters-ui/permissions/gates';
import invariant from 'react-utils/invariant';
import * as Operators from 'customer-data-filters-ui/filterQueryFormat/operator/Operators';
import * as TimeOperationPropertyParserTypes from 'customer-data-filters-ui/converters/objectSeg/timeFilters/TimeOperationPropertyParserTypes';
import * as TimeOperationRenderSpecTypes from 'customer-data-filters-ui/converters/objectSeg/timeFilters/TimeOperationRenderSpecTypes';
import * as TimeOperationEndpointBehaviorTypes from 'customer-data-filters-ui/converters/objectSeg/timeFilters/TimeOperationEndpointBehaviorTypes';
import { formatDurationFromTimepointToMilliseconds } from '../../../utils/durationUtilities';
import { DATE, DATE_TIME } from 'customer-data-objects/property/PropertyTypes';
import { maybeExtractPresetDynamicDateTimeOperation } from '../timeFilters/maybeExtractPresetDynamicDateTimeOperation';
const fromEqualToDateOperation = (field, operation) => {
  const momentDate = convertTimePointToMomentDate(operation.lowerBoundTimePoint);
  const hasTimezones = operation.propertyParser === TimeOperationPropertyParserTypes.VALUE;
  return Operators.EqualTimePoint.of(field, momentDate, hasTimezones);
};
const fromAfterDateOperation = (field, operation) => {
  const momentDate = convertTimePointToMomentDate(operation.timePoint);
  const hasTimezones = operation.propertyParser === TimeOperationPropertyParserTypes.VALUE;
  return Operators.AfterTimePoint.of(field, momentDate, hasTimezones);
};
const fromAfterDateWithTimeOperation = (field, operation) => {
  const momentDate = convertTimePointToMomentDateWithTime(operation.timePoint);
  return Operators.AfterTimePointWithTime.of(field, momentDate);
};
const fromBeforeDateOperation = (field, operation) => {
  const momentDate = convertTimePointToMomentDate(operation.timePoint);
  const hasTimezones = operation.propertyParser === TimeOperationPropertyParserTypes.VALUE;
  return Operators.BeforeTimePoint.of(field, momentDate, hasTimezones);
};
const fromBeforeDateWithTimeOperation = (field, operation) => {
  const momentDate = convertTimePointToMomentDateWithTime(operation.timePoint);
  return Operators.BeforeTimePointWithTime.of(field, momentDate);
};
const fromBetweenDatesOperation = (field, operation) => {
  const lowerBoundDateString = convertTimePointToMomentDate(operation.lowerBoundTimePoint);
  const upperBoundDateString = convertTimePointToMomentDate(operation.upperBoundTimePoint);
  const hasTimezones = operation.propertyParser === TimeOperationPropertyParserTypes.VALUE;
  return Operators.BetweenTimePoints.of(field, lowerBoundDateString, upperBoundDateString, hasTimezones, false);
};
const fromAnniversaryBetweenDatesOperation = (field, operation) => {
  const lowerBoundDateString = convertIndexedTimePointToDateWithoutYear(operation.lowerBoundTimePoint);
  const upperBoundDateString = convertIndexedTimePointToDateWithoutYear(operation.upperBoundTimePoint);
  const hasTimezones = operation.propertyParser === TimeOperationPropertyParserTypes.ANNIVERSARY;
  return Operators.BetweenTimePoints.of(field, lowerBoundDateString, upperBoundDateString, hasTimezones, true);
};
const fromBetweenDatesWithTimeOperation = (field, operation) => {
  const lowerBoundDateString = convertTimePointToMomentDateWithTime(operation.lowerBoundTimePoint);
  const upperBoundDateString = convertTimePointToMomentDateWithTime(operation.upperBoundTimePoint);
  return Operators.BetweenTimePointsWithTime.of(field, lowerBoundDateString, upperBoundDateString);
};
const fromNotBetweenDatesOperation = (field, operation) => {
  const lowerBoundDateString = convertTimePointToMomentDate(operation.lowerBoundTimePoint);
  const upperBoundDateString = convertTimePointToMomentDate(operation.upperBoundTimePoint);
  const hasTimezones = operation.propertyParser === TimeOperationPropertyParserTypes.VALUE;
  return Operators.NotBetweenTimePoints.of(field, lowerBoundDateString, upperBoundDateString, hasTimezones, false);
};
const fromAnniversaryNotBetweenDatesOperation = (field, operation) => {
  const lowerBoundDateString = convertIndexedTimePointToDateWithoutYear(operation.lowerBoundTimePoint);
  const upperBoundDateString = convertIndexedTimePointToDateWithoutYear(operation.upperBoundTimePoint);
  const hasTimezones = operation.propertyParser === TimeOperationPropertyParserTypes.ANNIVERSARY;
  return Operators.NotBetweenTimePoints.of(field, lowerBoundDateString, upperBoundDateString, hasTimezones, true);
};
const fromNotBetweenDatesWithTimeOperation = (field, operation) => {
  const lowerBoundDateString = convertTimePointToMomentDateWithTime(operation.lowerBoundTimePoint);
  const upperBoundDateString = convertTimePointToMomentDateWithTime(operation.upperBoundTimePoint);
  return Operators.NotBetweenTimePointsWithTime.of(field, lowerBoundDateString, upperBoundDateString);
};
const fromMoreThanXDaysAgoOperation = (field, operation) => {
  let days = operation.timePoint.offset.days;

  // Round up to nearest day when hours is set to 23 hours.
  // This is due to a bug in the FE code early on in releasing TimePoints.
  if (operation.timePoint.offset.hours === 23) {
    // Days are expected to be negative, but they can be positive
    days = days > 0 ? days + 1 : days - 1;
  }

  // The ILS format encodes direction into the number as the sign.
  // -X means backwards, X means forward
  // So 10 days forwards is -10 backwards
  // Since this operator forces the the units of "days" to be based in the backwards direction days has to be converted from "forward days" to "backwards days".
  // forward days = -backwards days
  days = -days;
  const hasTimezones = operation.propertyParser === TimeOperationPropertyParserTypes.VALUE;
  return Operators.GreaterRolling.of(field, days, 'backward', operation.timePoint.zoneId, hasTimezones);
};
const fromMoreThanXDaysFromNowOperation = (field, operation) => {
  let days = operation.timePoint.offset.days;

  // Round up to nearest day when hours is set to 23 hours.
  // This is due to a bug in the FE code early on in releasing TimePoints.
  if (operation.timePoint.offset.hours === 23) {
    // Days are expected to be positive, but they can be negative
    days = days > 0 ? days + 1 : days - 1;
  }
  const hasTimezones = operation.propertyParser === TimeOperationPropertyParserTypes.VALUE;
  return Operators.GreaterRolling.of(field, days, 'forward', operation.timePoint.zoneId, hasTimezones);
};
const fromLessThanXDaysAgoOperation = (field, operation) => {
  var _operation$lowerBound, _operation$lowerBound2, _operation$lowerBound3;
  let days = Math.abs((_operation$lowerBound = (_operation$lowerBound2 = operation.lowerBoundTimePoint.offset) === null || _operation$lowerBound2 === void 0 ? void 0 : _operation$lowerBound2.days) !== null && _operation$lowerBound !== void 0 ? _operation$lowerBound : 0);
  if (((_operation$lowerBound3 = operation.lowerBoundTimePoint.offset) === null || _operation$lowerBound3 === void 0 ? void 0 : _operation$lowerBound3.hours) === 23) {
    days++;
  }
  const hasTimezones = operation.propertyParser === TimeOperationPropertyParserTypes.VALUE || operation.propertyParser === TimeOperationPropertyParserTypes.ANNIVERSARY;
  const ignoreYear = operation.propertyParser === TimeOperationPropertyParserTypes.ANNIVERSARY || operation.propertyParser === TimeOperationPropertyParserTypes.ANNIVERSARY_WITH_ZONE_SAME_LOCAL_CONVERSION;
  return Operators.LessRollingWithOrWithoutYear.of(field, days, 'backward', operation.lowerBoundTimePoint.zoneId, hasTimezones, ignoreYear);
};
const fromLessThanXDaysFromNowOperation = (field, operation) => {
  var _operation$upperBound, _operation$upperBound2, _operation$upperBound3;
  let days = Math.abs((_operation$upperBound = (_operation$upperBound2 = operation.upperBoundTimePoint.offset) === null || _operation$upperBound2 === void 0 ? void 0 : _operation$upperBound2.days) !== null && _operation$upperBound !== void 0 ? _operation$upperBound : 0);
  if (((_operation$upperBound3 = operation.upperBoundTimePoint.offset) === null || _operation$upperBound3 === void 0 ? void 0 : _operation$upperBound3.hours) === 23) {
    days++;
  }
  const hasTimezones = operation.propertyParser === TimeOperationPropertyParserTypes.VALUE || operation.propertyParser === TimeOperationPropertyParserTypes.ANNIVERSARY;
  const ignoreYear = operation.propertyParser === TimeOperationPropertyParserTypes.ANNIVERSARY || operation.propertyParser === TimeOperationPropertyParserTypes.ANNIVERSARY_WITH_ZONE_SAME_LOCAL_CONVERSION;
  return Operators.LessRollingWithOrWithoutYear.of(field, days, 'forward', operation.lowerBoundTimePoint.zoneId, hasTimezones, ignoreYear);
};
const fromBeforeDateOperationForRelativeDates = (field, operation) => {
  const {
    renderSpec
  } = operation;
  const {
    relativeTimeCalculationType
  } = renderSpec && JSON.parse(renderSpec);
  let valueInMilliseconds = formatDurationFromTimepointToMilliseconds(operation.timePoint.offset);
  if (relativeTimeCalculationType === TimeOperationRenderSpecTypes.TIME_SINCE) {
    valueInMilliseconds = -valueInMilliseconds;
  }
  const isEndpointBehaviorInclusive = operation.endpointBehavior === TimeOperationEndpointBehaviorTypes.INCLUSIVE;
  if (relativeTimeCalculationType === TimeOperationRenderSpecTypes.TIME_SINCE) {
    return isEndpointBehaviorInclusive ? Operators.GreaterOrEqual.of(field, valueInMilliseconds) : Operators.Greater.of(field, valueInMilliseconds);
  } else {
    return isEndpointBehaviorInclusive ? Operators.LessOrEqual.of(field, valueInMilliseconds) : Operators.Less.of(field, valueInMilliseconds);
  }
};
const fromAfterDateOperationForRelativeDates = (field, operation) => {
  const {
    renderSpec
  } = operation;
  const {
    relativeTimeCalculationType
  } = renderSpec && JSON.parse(renderSpec);
  let valueInMilliseconds = formatDurationFromTimepointToMilliseconds(operation.timePoint.offset);
  if (relativeTimeCalculationType === TimeOperationRenderSpecTypes.TIME_SINCE) {
    valueInMilliseconds = -valueInMilliseconds;
  }
  const isEndpointBehaviorInclusive = operation.endpointBehavior === TimeOperationEndpointBehaviorTypes.INCLUSIVE;
  if (relativeTimeCalculationType === TimeOperationRenderSpecTypes.TIME_SINCE) {
    return isEndpointBehaviorInclusive ? Operators.LessOrEqual.of(field, valueInMilliseconds) : Operators.Less.of(field, valueInMilliseconds);
  } else {
    return isEndpointBehaviorInclusive ? Operators.GreaterOrEqual.of(field, valueInMilliseconds) : Operators.Greater.of(field, valueInMilliseconds);
  }
};
const fromIsBetweenDateOperationForRelativeDate = (field, operation) => {
  var _operation$upperBound4;
  const {
    renderSpec
  } = operation;
  const {
    relativeTimeCalculationType
  } = renderSpec && JSON.parse(renderSpec);
  let valueInMilliseconds = formatDurationFromTimepointToMilliseconds((_operation$upperBound4 = operation.upperBoundTimePoint.offset) !== null && _operation$upperBound4 !== void 0 ? _operation$upperBound4 : {});
  if (relativeTimeCalculationType === TimeOperationRenderSpecTypes.TIME_SINCE) {
    valueInMilliseconds = -valueInMilliseconds;
  }
  return Operators.Equal.of(field, valueInMilliseconds);
};
const fromIsNotBetweenDateOperationForRelativeDate = (field, operation) => {
  var _operation$upperBound5;
  const {
    renderSpec
  } = operation;
  const {
    relativeTimeCalculationType
  } = renderSpec && JSON.parse(renderSpec);
  let valueInMilliseconds = formatDurationFromTimepointToMilliseconds((_operation$upperBound5 = operation.upperBoundTimePoint.offset) !== null && _operation$upperBound5 !== void 0 ? _operation$upperBound5 : {});
  if (relativeTimeCalculationType === TimeOperationRenderSpecTypes.TIME_SINCE) {
    valueInMilliseconds = -valueInMilliseconds;
  }
  return Operators.NotEqual.of(field, valueInMilliseconds);
};
const fromAfterPropertyOperation = (field, operation) => {
  return Operators.After.of(field, operation.timePoint.property, operation.timePoint.referenceType === TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION ? DATE : DATE_TIME);
};
const fromBeforePropertyOperation = (field, operation) => {
  return Operators.Before.of(field, operation.timePoint.property, operation.timePoint.referenceType === TimeOperationPropertyParserTypes.VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION ? DATE : DATE_TIME);
};
const fromUpdatedInLastXDaysAgoOperation = (field, operation) => {
  var _operation$lowerBound4, _operation$lowerBound5;
  return Operators.UpdatedInLastXDays.of(field, Math.abs((_operation$lowerBound4 = (_operation$lowerBound5 = operation.lowerBoundTimePoint.offset) === null || _operation$lowerBound5 === void 0 ? void 0 : _operation$lowerBound5.days) !== null && _operation$lowerBound4 !== void 0 ? _operation$lowerBound4 : 0));
};
const fromNotUpdatedInLastXDaysAgoOperation = (field, operation) => {
  var _operation$lowerBound6, _operation$lowerBound7;
  return Operators.NotUpdatedInLastXDays.of(field, Math.abs((_operation$lowerBound6 = (_operation$lowerBound7 = operation.lowerBoundTimePoint.offset) === null || _operation$lowerBound7 === void 0 ? void 0 : _operation$lowerBound7.days) !== null && _operation$lowerBound6 !== void 0 ? _operation$lowerBound6 : 0));
};
const fromUpdatedAfterAnotherPropertyOperation = (field, operation) => {
  return Operators.UpdatedAfter.of(field, operation.timePoint.property);
};
const fromUpdatedBeforeAnotherPropertyOperation = (field, operation) => {
  return Operators.UpdatedBefore.of(field, operation.timePoint.property);
};
export const fromTimeOperation = (__Operator, field, filter, options) => {
  const isUngatedForNewTimeFilters = hasNewIlsTimeFiltersEnabled(options.isUngated);
  invariant(isUngatedForNewTimeFilters, 'Not ungated for a new time filters format');
  const isUsingHoursFiltering = hasDateTimeHoursFiltering(options.isUngated) && options.useHoursFiltering;
  if (hasILSPresetDynamicDateFilters(options.isUngated)) {
    // When removing the gate check, also remove the redundant deserialization at the end of this function.
    // Preset dynamic date operations are a special case of isBetweenDatesOperation is so this needs to be above that case
    const maybePresetDynamicDateTimeOperation = maybeExtractPresetDynamicDateTimeOperation(field, filter.operation);
    if (maybePresetDynamicDateTimeOperation) {
      return maybePresetDynamicDateTimeOperation;
    }
  }
  if (isEqualToDateOperation(filter.operation, isUsingHoursFiltering)) {
    return fromEqualToDateOperation(field, filter.operation);
  }
  if (isUsingHoursFiltering) {
    if (isAfterDateWithTimeOperation(filter.operation)) {
      return fromAfterDateWithTimeOperation(field, filter.operation);
    }
    if (isBeforeDateWithTimeOperation(filter.operation)) {
      return fromBeforeDateWithTimeOperation(field, filter.operation);
    }
    if (isBetweenDatesWithTimeOperation(filter.operation)) {
      return fromBetweenDatesWithTimeOperation(field, filter.operation);
    }
    if (isNotBetweenDatesWithTimeOperation(filter.operation)) {
      return fromNotBetweenDatesWithTimeOperation(field, filter.operation);
    }
  } else {
    if (isAfterDateOperation(filter.operation)) {
      return fromAfterDateOperation(field, filter.operation);
    }
    if (isBeforeDateOperation(filter.operation)) {
      return fromBeforeDateOperation(field, filter.operation);
    }
    if (isBetweenDatesOperation(filter.operation)) {
      return fromBetweenDatesOperation(field, filter.operation);
    }
    if (isNotBetweenDatesOperation(filter.operation)) {
      return fromNotBetweenDatesOperation(field, filter.operation);
    }
  }
  if (isAnniversaryBetweenDatesOperation(filter.operation)) {
    return fromAnniversaryBetweenDatesOperation(field, filter.operation);
  }
  if (isAnniversaryNotBetweenDatesOperation(filter.operation)) {
    return fromAnniversaryNotBetweenDatesOperation(field, filter.operation);
  }
  if (isAfterDateOperationForRelativeDates(filter.operation)) {
    return fromAfterDateOperationForRelativeDates(field, filter.operation);
  }
  if (isBeforeDateOperationForRelativeDates(filter.operation)) {
    return fromBeforeDateOperationForRelativeDates(field, filter.operation);
  }
  if (isEqualToRelativeDateOperation(filter.operation)) {
    return fromIsBetweenDateOperationForRelativeDate(field, filter.operation);
  }
  if (isNotEqualToRelativeDateOperation(filter.operation)) {
    return fromIsNotBetweenDateOperationForRelativeDate(field, filter.operation);
  }
  if (isMoreThanXDaysAgoOperation(filter.operation)) {
    return fromMoreThanXDaysAgoOperation(field, filter.operation);
  }
  if (isMoreThanXDaysFromNowOperation(filter.operation)) {
    return fromMoreThanXDaysFromNowOperation(field, filter.operation);
  }
  if (isLessThanXDaysAgoOperation(filter.operation)) {
    return fromLessThanXDaysAgoOperation(field, filter.operation);
  }
  if (isLessThanXDaysFromNowOperation(filter.operation)) {
    return fromLessThanXDaysFromNowOperation(field, filter.operation);
  }
  if (isAfterPropertyOperation(filter.operation)) {
    return fromAfterPropertyOperation(field, filter.operation);
  }
  if (isBeforePropertyOperation(filter.operation)) {
    return fromBeforePropertyOperation(field, filter.operation);
  }
  if (isUpdatedInLastXDaysOperation(filter.operation)) {
    return fromUpdatedInLastXDaysAgoOperation(field, filter.operation);
  }
  if (isNotUpdatedInLastXDaysOperation(filter.operation)) {
    return fromNotUpdatedInLastXDaysAgoOperation(field, filter.operation);
  }
  if (isUpdatedAfterPropertyOperation(filter.operation)) {
    return fromUpdatedAfterAnotherPropertyOperation(field, filter.operation);
  }
  if (isUpdatedBeforePropertyOperation(filter.operation)) {
    return fromUpdatedBeforeAnotherPropertyOperation(field, filter.operation);
  }

  // Remove this when removing the `hasILSPresetDynamicDateFilters` gate check.
  // Repeat preset dynamic date deserialization
  //   - WITHOUT the gate check
  //   - AFTER all other deserialization logic
  // This way, if re-gated, any persisted dynamic date operations are deserialized.
  //   The condition will render with an error.
  //   Without this, `undefined` is returned, which will crash the filter editor.
  const maybePresetDynamicDateTimeOperation = maybeExtractPresetDynamicDateTimeOperation(field, filter.operation);
  if (maybePresetDynamicDateTimeOperation) {
    return maybePresetDynamicDateTimeOperation;
  }
  return undefined;
};